import React from 'react';
import { Helmet } from 'react-helmet';
import { navigate, RouteComponentProps } from '@reach/router';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { RootState } from 'store/store';
import Input from 'components/Input';
import Button from 'components/Button';
import { display } from 'components/ErrorView/ErrorDialog';
import notify from 'components/Notify';
import { appDispatch } from 'store/store';
import { ICompany } from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import {
  createCompany,
  createCompanyFn,
  updateCompany,
  updateCompanyFn,
} from 'store/companies/companiesSlice';
import {
  isCompanyOwner,
  isCompanySupport,
} from 'store/companies/companiesUtils';
import i18n from '../../../i18n';
import Select from '../../../components/Select';
import { IOption } from '../../../components/Select/Select';
import { exactWhite } from 'utils/style/colors';

interface IStateToProps {
  company: ICompany;
  thisUser: IThisUserState;
}

interface IDispatchToProps {
  createCompany: (company: ICompany) => ReturnType<typeof createCompanyFn>;
  updateCompany: (company: ICompany) => ReturnType<typeof updateCompanyFn>;
}

interface IOwnProps {
  companyId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface IComponentStates {
  name: string;
  saving: boolean;
  selectedCompanyLanguage: string | undefined;
  selectedTimeZone: string | undefined;
  domain?: string;
  limitViewToDomain?: boolean;
}

const timezoneOptions: IOption[] = [
  {
    label: 'UTC-12:00  (Baker Island, Howland Island - uninhabited)',
    value: 'UTC-12:00',
  },
  {
    label: 'UTC-11:00  (American Samoa, Niue, Midway Atoll)',
    value: 'UTC-11:00',
  },
  {
    label: 'UTC-10:00  (Hawaii, Cook Islands, Tahiti)',
    value: 'UTC-10:00',
  },
  {
    label: 'UTC-09:30  (Marquesas Islands - French Polynesia)',
    value: 'UTC-09:30',
  },
  {
    label: 'UTC-09:00  (Alaska, Gambier Islands)',
    value: 'UTC-09:00',
  },
  {
    label: 'UTC-08:00  (Pacific Time - USA, Canada, Mexico)',
    value: 'UTC-08:00',
  },
  {
    label: 'UTC-07:00  (Mountain Time - USA, Canada, Mexico)',
    value: 'UTC-07:00',
  },
  {
    label: 'UTC-06:00  (Central Time - USA, Canada, Mexico, Central America)',
    value: 'UTC-06:00 ',
  },
  {
    label: 'UTC-05:00  (Eastern Time - USA, Canada, Colombia, Cuba)',
    value: 'UTC-05:00',
  },
  {
    label: 'UTC-04:30  (Venezuela Time - VET) [Discontinued in 2016]',
    value: 'UTC-04:30',
  },
  {
    label: 'UTC-04:00  (Atlantic Time - Canada, Chile, Bolivia, Paraguay)',
    value: 'UTC-04:00',
  },
  {
    label: 'UTC-03:30  (Newfoundland Time - Canada)',
    value: 'UTC-03:30',
  },
  {
    label: 'UTC-03:00  (Argentina, Brazil, Uruguay, French Guiana)',
    value: 'UTC-03:00',
  },
  {
    label: 'UTC-02:30  (St. Pierre & Miquelon - France)',
    value: 'UTC-02:30',
  },
  {
    label: 'UTC-02:00  (South Georgia & South Sandwich Islands)',
    value: 'UTC-02:00 ',
  },
  {
    label: 'UTC-01:00  (Azores - Portugal, Cape Verde)',
    value: 'UTC-01:00',
  },
  {
    label: 'UTC (Greenwich Mean Time - UK, Iceland, Portugal, Senegal)',
    value: 'UTC',
  },
  {
    label: 'UTC+01:00  (Central European Time - Germany, France, Nigeria)',
    value: 'UTC+01:00',
  },
  {
    label: 'UTC+02:00  (Eastern European Time - Egypt, South Africa, Ukraine)',
    value: 'UTC+02:00',
  },
  {
    label: 'UTC+03:00  (Moscow Time - Russia, Saudi Arabia, East Africa)',
    value: 'UTC+03:00',
  },
  {
    label: 'UTC+03:30  (Iran Standard Time - Iran)',
    value: 'UTC+03:30',
  },
  {
    label: 'UTC+04:00  (United Arab Emirates, Azerbaijan, Mauritius)',
    value: 'UTC+04:00',
  },
  {
    label: 'UTC+04:30  (Afghanistan)',
    value: 'UTC+04:30',
  },
  {
    label: 'UTC+05:00  (Pakistan, Uzbekistan, Maldives)',
    value: 'UTC+05:00',
  },
  {
    label: 'UTC+05:30  (India, Sri Lanka)',
    value: 'UTC+05:30',
  },
  {
    label: 'UTC+05:45  (Nepal)',
    value: 'UTC+05:45',
  },
  {
    label: 'UTC+06:00  (Bangladesh, Bhutan, Kazakhstan)',
    value: 'UTC+06:00',
  },
  {
    label: 'UTC+06:30  (Myanmar, Cocos Islands)',
    value: 'UTC+06:30',
  },
  {
    label: 'UTC+07:00  (Thailand, Vietnam, Cambodia, Indonesia)',
    value: 'UTC+07:00',
  },
  {
    label: 'UTC+08:00  (China, Malaysia, Singapore, Western Australia)',
    value: 'UTC+08:00',
  },
  {
    label: 'UTC+08:30  (North Korea) [Rare, unofficial]',
    value: 'UTC+08:30',
  },
  {
    label: 'UTC+08:45  (Eucla - Western Australia) [Very rare]',
    value: 'UTC+08:45',
  },
  {
    label: 'UTC+09:00  (Japan, South Korea, Eastern Russia)',
    value: 'UTC+09:00',
  },
  {
    label:
      'UTC+09:30  (Central Australia - Northern Territory, South Australia)',
    value: 'UTC+09:30',
  },
  {
    label: 'UTC+10:00  (Eastern Australia, Papua New Guinea, Vladivostok)',
    value: 'UTC+10:00',
  },
  {
    label: 'UTC+10:30  (Lord Howe Island - Australia)',
    value: 'UTC+10:30',
  },
  {
    label: 'UTC+11:00  (Solomon Islands, New Caledonia)',
    value: 'UTC+11:00',
  },
  {
    label: 'UTC+12:00  (New Zealand, Fiji, Kamchatka - Russia)',
    value: 'UTC+12:00',
  },
  {
    label: 'UTC+12:45  (Chatham Islands - New Zealand)',
    value: 'UTC+12:45',
  },
  {
    label: 'UTC+13:00  (Tonga, Samoa)',
    value: 'UTC+13:00 ',
  },
  {
    label: 'UTC+14:00  (Line Islands - Kiribati)',
    value: 'UTC+14:00',
  },
];

const PERSIAN_LANGUAGE_FLAG = 'fa_IR@calendar=persian';

class CompanyEditor extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      name: this.props.company ? this.props.company.name : '',
      saving: false,
      selectedCompanyLanguage: this.props.company?.local,
      selectedTimeZone: this.props.company?.timeZone,
      domain: this.props.company?.domain,
      limitViewToDomain: this.props.company?.limitViewToDomain,
    };
  }

  languageOptions: IOption[] = [
    {
      label: i18n.t('persian', { ns: 'companyManagement' }),
      value: PERSIAN_LANGUAGE_FLAG,
    },
    { label: i18n.t('english', { ns: 'companyManagement' }), value: 'en' },
  ];

  componentDidMount() {
    const isSupport = isCompanySupport(this.props.thisUser, this.props.company);
    const isOwner =
      !this.props.company ||
      isCompanyOwner(this.props.thisUser, this.props.company);
    if (this.props.company && !isOwner && !isSupport) {
      navigate('/notfound');
    }
  }

  handleChangeLanguage = (values: IOption[]) => {
    this.setState((prevState) => ({
      selectedCompanyLanguage: (values[0].value as string) || '',
      selectedTimeZone:
        (values[0].value as string) === PERSIAN_LANGUAGE_FLAG
          ? 'UTC+03:30'
          : prevState.selectedTimeZone,
    }));
  };

  handleChangeTimezone = (values: IOption[]) => {
    this.setState({
      selectedTimeZone: (values[0].value as string) || '',
    });
  };

  handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.type === 'checkbox') {
        this.setState({
          [field]: event.target.checked,
        } as any);
      } else {
        this.setState({
          [field]: event.target.value,
        } as any);
      }
    };

  handleSave = () => {
    if (this.state.name.length > 0) {
      this.setState({ saving: true });
      if (this.props.company) {
        const company = {
          id: this.props.company.id,
          name: this.state.name,
          owner: this.props.company.owner,
          local: this.state.selectedCompanyLanguage,
          timeZone: this.state.selectedTimeZone,
          domain: this.state.domain,
          limitViewToDomain: this.state.limitViewToDomain,
        } as ICompany;
        this.props
          .updateCompany(company)
          .then(() => {
            navigate('/c/company/list');
            notify(i18n.t('companyEdited', { ns: 'companyManagement' }));
          })
          .catch((err: any) => {
            this.setState({ saving: false });
            display(err);
          });
      } else {
        const company = {
          name: this.state.name,
          local: this.state.selectedCompanyLanguage,
          timeZone: this.state.selectedTimeZone,
          domain: this.state.domain,
          limitViewToDomain: this.state.limitViewToDomain,
        } as ICompany;
        this.props
          .createCompany(company)
          .then(() => {
            navigate('/c/company/list');
            notify(i18n.t('companyCreated', { ns: 'companyManagement' }));
          })
          .catch((err: any) => {
            this.setState({ saving: false });
            display(err);
          });
      }
    }
  };

  handleCancel = () => {
    navigate('/c/company/list');
  };

  render() {
    const showMultiLang = false;
    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          padding: '1rem 0.5rem',
          background: exactWhite,
        }}
      >
        <Helmet>
          <title>
            {i18n.t('companyEditor', { ns: 'companyManagement' }) as string}
          </title>
          <meta
            name="description"
            content={
              i18n.t('companyEditor', { ns: 'companyManagement' }) as string
            }
          />
        </Helmet>
        <div
          className="section"
          style={{
            height: '100%',
            gap: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            padding: '0 0.5rem',
          }}
        >
          <div className="">
            <Input
              type="text"
              id="title"
              name="title"
              className="validate"
              required={true}
              value={this.state.name}
              onChange={this.handleChange('name')}
              label={
                i18n.t('companyTitle', { ns: 'companyManagement' }) as string
              }
              placeholder={
                i18n.t('companyTitle', { ns: 'companyManagement' }) as string
              }
            />
          </div>
          <div className="">
            <Input
              type="text"
              id="title"
              name="title"
              className="validate"
              required={false}
              value={this.state.domain}
              onChange={this.handleChange('domain')}
              label={i18n.t('domain', { ns: 'companyManagement' }) as string}
              placeholder={'sample: ' + window.location.hostname}
            />
          </div>
          <div className="">
            <Input
              type="checkbox"
              id="title"
              name="title"
              disabled={!this.state.domain}
              className="validate"
              required={false}
              checked={this.state.limitViewToDomain}
              onChange={this.handleChange('limitViewToDomain')}
              label={
                i18n.t('limitViewToDomain', {
                  ns: 'companyManagement',
                }) as string
              }
              placeholder={
                i18n.t('limitViewToDomain', {
                  ns: 'companyManagement',
                }) as string
              }
            />
          </div>
          {showMultiLang && (
            <div className="flex items-center w-full gap-5">
              <div style={{ width: '30%' }}>
                <Select
                  options={this.languageOptions}
                  values={this.languageOptions.filter(
                    (item: IOption) =>
                      item.value === this.state.selectedCompanyLanguage
                  )}
                  onChangeValues={this.handleChangeLanguage}
                  multiChoice={false}
                  allowRemove={false}
                />
              </div>
              <div style={{ width: '70%' }}>
                <Select
                  options={timezoneOptions}
                  values={timezoneOptions.filter(
                    (item: IOption) =>
                      item.value === this.state.selectedTimeZone
                  )}
                  onChangeValues={this.handleChangeTimezone}
                  multiChoice={false}
                  allowRemove={false}
                  disabled={
                    this.state.selectedCompanyLanguage === PERSIAN_LANGUAGE_FLAG
                  }
                />
              </div>
            </div>
          )}
          <div className="flex gap-3 justify-end">
            <Button
              onClick={this.handleSave}
              loading={this.state.saving}
              color="green"
            >
              {i18n.t('save', { ns: 'companyManagement' }) as string}
            </Button>

            <Button onClick={this.handleCancel} color="red">
              {i18n.t('cancel', { ns: 'companyManagement' }) as string}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  let companyId = 0;
  if (props.companyId) {
    companyId = Number(props.companyId);
  }
  const company = state.companies.data[companyId];
  const thisUser = state.thisUser;

  return { company, thisUser };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => ({
  updateCompany: (updatedCompany: ICompany) =>
    appDispatch(updateCompany({ company: updatedCompany })),
  createCompany: (createdCompany: ICompany) =>
    appDispatch(createCompany({ company: createdCompany })),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyEditor);
